import { Component, OnInit, Input } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatDialog } from '@angular/material/dialog';
import { NotesService } from '../../../services/notes.service';
import { NoteBuilderComponent } from '../note-builder/note-builder.component';
import { NoteBoilerplatesComponent } from '../note-boilerplates/note-boilerplates.component';
import { MessagingService } from '../../../services/messaging.service';
import { NoteDataModel } from '../../../models/notes/note-data.model';
import { NoteCategoryDataModel } from '../../../models/notes/note-category-data.model';
import { EnumValidValueItemModel } from '../../../models/enum/enum-valid-value-item.model';
import { EnumValidValueModel } from '../../../models/enum/enum-valid-value.model';
import { NoteTypeDataModel } from '../../../models/notes/note-type-data.model';
import { DialogService } from 'src/app/services/dialog.service';

@Component({
  selector: 'app-note-viewer',
  templateUrl: './note-viewer.component.html',
  styleUrls: ['./note-viewer.component.css']
})
export class NoteViewerComponent implements OnInit {

  @Input() dataNote: NoteDataModel;
  @Input() editable: boolean = false;
  @Input() dataNoteCategoryEnums: EnumValidValueModel;
  @Input() dataNoteTypeEnums: EnumValidValueModel;
  @Input() compactView: boolean = false;

  saveTimer: any;
  saveSubscription: any;
  isSaving: boolean = false;
  compactMode: boolean = false;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  public selectedCategories: string[];
  public selectedTypes: string[];

  constructor(
    private notesService: NotesService,
    public dialog: MatDialog,
    private dialogService: DialogService,
    private messagingService: MessagingService,
  ) { }

  private distinct(value, index, self) {
    return self.indexOf(value) === index;
  }

  ngOnInit() {
    this.selectedCategories = this.dataNote.dataNoteCategories.filter(o => !o.xauditDeleted)
      .map(o => o.enumNoteCategory.ValidValueItemId)
      .filter(this.distinct);

    this.selectedTypes = this.dataNote.dataNoteTypes.filter(o => !o.xauditDeleted)
      .map(o => o.enumNoteType.ValidValueItemId)
      .filter(this.distinct);

    const nowDate = new Date();
    const createdDate = this.convertUTCDateToLocalDateFromString(this.dataNote.xauditCreatedOn);
    const hourDiff = this.diff_hours(nowDate, createdDate);

    if (hourDiff >= 24) {
      this.editable = false;
    }

    this.filterCategories();
  }

  diff_hours(dt2, dt1) {
    let diff = (dt2.getTime() - dt1.getTime()) / 1000;
    diff /= (60 * 60);
    return Math.abs(Math.round(diff));
  }

  convertUTCDateToLocalDateFromString(dateString) {
    if (!dateString) {
      return null;
    }

    const date = new Date(dateString);
    return this.convertUTCDateToLocalDate(date);
  }

  convertUTCDateToLocalDate(date) {
    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
      )
    );
  }

  filterCategories() {
    this.dataNote.dataNoteCategories = this.dataNote.dataNoteCategories.filter(c => !c.xauditDeleted);
  }

  removeCategory(category: NoteCategoryDataModel) {
    const bkCategory: NoteCategoryDataModel = JSON.parse(JSON.stringify(category));
    this.dataNote.dataNoteCategories.splice(this.dataNote.dataNoteCategories.indexOf(category), 1);

    delete bkCategory.enumNoteCategory;
    bkCategory.xauditDeleted = true;

    this.saveCategory(bkCategory)
      .then(result => {
      }, err => {
        console.log('err', err);
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Whoops... something went wrong'
        });
      });
  }

  removeType(type: NoteTypeDataModel) {
    const bkType: NoteTypeDataModel = JSON.parse(JSON.stringify(type));
    this.dataNote.dataNoteCategories.splice(this.dataNote.dataNoteTypes.indexOf(type), 1);

    delete bkType.enumNoteType;
    bkType.xauditDeleted = true;

    this.saveType(bkType)
      .then(result => {
      }, err => {
        console.log('err', err);
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Whoops... something went wrong'
        });
      });
  }

  saveCategory(category: NoteCategoryDataModel) {
    const promise = new Promise((resolve, reject) => {
      this.notesService.saveCategory(category)
        .subscribe(response => {
          resolve(response);
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  saveType(type: NoteTypeDataModel) {
    return this.notesService.saveType(type).toPromise();
  }

  handleSaveNote(announceSave = true) {
    this.isSaving = true;
    const bkNote: NoteDataModel = JSON.parse(JSON.stringify(this.dataNote));
    bkNote.dataNoteCategories = [];
    bkNote.dataNoteTypes = [];

    this.saveNote(bkNote)
      .then((result: NoteDataModel) => {
        this.isSaving = false;
        if (announceSave) {
          this.messagingService.sendComponentMessage('notesDisplaySuccess', {
            message: 'Note saved'
          });
        }
      }, err => {
        console.log('err', err);
        this.isSaving = false;
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Error saving note'
        });
      });
  }

  saveNote(note: NoteDataModel) {
    const promise = new Promise((resolve, reject) => {

      if (this.saveSubscription) {
        this.saveSubscription.unsubscribe();
      }

      const noteClone = JSON.parse(JSON.stringify(note));
      noteClone.xauditCreatedByUser = null;
      noteClone.xauditModifiedByUser = null;

      this.saveSubscription = this.notesService.saveDataNote(noteClone)
        .subscribe(response => {
          this.saveSubscription = false;
          resolve(response);
        }, err => {
          this.saveSubscription = false;
          reject(err);
        });
    });
    return promise;
  }

  handleAddingCategory(category: NoteCategoryDataModel) {
    const bkCategory: NoteCategoryDataModel = JSON.parse(JSON.stringify(category));
    delete bkCategory.enumNoteCategory;
    this.dataNote.dataNoteCategories.push(category);

    this.saveCategory(bkCategory)
      .then((result: NoteCategoryDataModel) => {
        result.enumNoteCategory = category.enumNoteCategory;
        category = result;
      }, err => {
        console.log(err);
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Whoops... something went wrong'
        });
      });
  }

  categoryAlreadyExists(itemValidValueItemId: string): boolean {
    const foundCategories = this.dataNote.dataNoteCategories.filter(c => {
      return c.enumNoteCategory.ValidValueItemId === itemValidValueItemId;
    });
    return foundCategories.length !== 0;
  }

  createCategoryObjectFromEnum(enumValidValueItem: EnumValidValueItemModel): NoteCategoryDataModel {
    const dataCategory = new NoteCategoryDataModel();
    dataCategory.NoteId = this.dataNote.NoteId;
    dataCategory.enumNoteCategoryID = enumValidValueItem.ValidValueItemId;
    dataCategory.enumNoteCategory = enumValidValueItem;
    return dataCategory;
  }

  onCatChange(categoryIds: any[]) {
    const foundCategories = this.dataNoteCategoryEnums.enumValidValueItems
      .filter(o => categoryIds.some(p => p === o.ValidValueItemId))
      .map(o => this.createCategoryObjectFromEnum(o));

    const currCategories: NoteCategoryDataModel[] = Array.from(this.dataNote.dataNoteCategories);

    // It was easier and faster to remove all the categories
    // and then add the ones we want back.
    // could be updated filter out the ones specifically we want to remove if we want to fine tune it
    currCategories.forEach(o => this.removeCategory(o));
    foundCategories.forEach(o => this.handleAddingCategory(o));
  }

  handleAddingType(type: NoteTypeDataModel) {
    const bkType: NoteTypeDataModel = JSON.parse(JSON.stringify(type));
    delete bkType.enumNoteType;
    this.dataNote.dataNoteTypes.push(type);

    this.saveType(bkType)
      .then((result: NoteTypeDataModel) => {
        result.enumNoteType = type.enumNoteType;
        type = result;
      }, err => {
        console.log(err);
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Whoops... something went wrong'
        });
      });
  }

  createTypeObjectFromEnum(enumValidValueItem: EnumValidValueItemModel): NoteTypeDataModel {
    const dataType = new NoteTypeDataModel();
    dataType.NoteId = this.dataNote.NoteId;
    dataType.enumNoteTypeID = enumValidValueItem.ValidValueItemId;
    dataType.enumNoteType = enumValidValueItem;
    return dataType;
  }

  onTypeChanged(typeIds: any[]): void {
    const foundTypes = this.dataNoteTypeEnums.enumValidValueItems
      .filter(o => typeIds.some(p => p === o.ValidValueItemId))
      .map(o => this.createTypeObjectFromEnum(o));

    const currTypes: NoteTypeDataModel[] = Array.from(this.dataNote.dataNoteTypes);

    // It was easier and faster to remove all the categories
    // and then add the ones we want back.
    // could be updated filter out the ones specifically we want to remove if we want to fine tune it
    currTypes.forEach(o => this.removeType(o));
    foundTypes.forEach(o => this.handleAddingType(o));
  }

  openNoteBuilderDialog(): void {
    console.log("openNoteBuilderDialog");

    const dialogRef = this.dialog.open(NoteBuilderComponent, {
      width: '1500px',
      height: '850px',
      data: {
        note: this.dataNote.Note + ' ',
      }
    });
    dialogRef.afterClosed().subscribe(
      result => {

        console.log("openNoteBuilderDialog closed: ", result);
        if (result && result.note) {
          this.dataNote.Note = result.note;
          this.handleSaveNote();
        }
      },
      err => {
        console.log('err', err);
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Whoops... something went wrong'
        });
      }
    );
  }

  openNoteBoilerplateDialog(): void {
    console.log("openNoteBoilerplateDialog");
    const dialogRef = this.dialog.open(NoteBoilerplatesComponent, {
      width: '1000px',
      height: '650px',
      data: {
        note: this.dataNote.Note + ' ',
      }
    });
    dialogRef.afterClosed().subscribe(
      result => {

        console.log("openNoteBoilerplateDialog closed:", result);
        if (result && result.note) {
          this.dataNote.Note = result.note;
          this.handleSaveNote();
        }
      },
      err => {
        console.log('err', err);
        this.messagingService.sendComponentMessage('notesDisplayWarning', {
          message: 'Whoops... something went wrong'
        });
      }
    );
  }

  deleteNote(): void {
    this.dialogService.openConfirmDialog(`Are you sure you want to delete this note?`).subscribe((result) => {
      if (result === true) {
        this.dataNote.xauditDeleted = true;
        this.handleSaveNote();
      }
    });
  }

  getPreview(note) {
    if (note.length > 100) {
      return note.slice(0, 99) + '...';
    } else {
      return note;
    }
  }

  startSaveTimer() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    this.saveTimer = setTimeout(_ => {
      this.handleSaveNote(false);
    }, 500);
  }
}

